import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import {
  IntegrationIcon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  RichTextEditor,
} from "@cohesive/ui-components";
import {
  ExternalLink,
  Info,
  RefreshCw,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNotionConnection } from "./hooks/useNotionConnection";
import { useNotionPageFetch } from "./hooks/useNotionPageFetch";
import { useNotionPageUpdate } from "./hooks/useNotionPageUpdate";

function sanitizeNotionContent(content: string) {
  let sanitized = content;

  // Replace tables with elegant placeholder
  sanitized = sanitized.replace(
    /<table[^>]*>[\s\S]*?<\/table>/gi,
    "\n\n📊 **[Table - Open in Notion to view]**\n\n",
  );

  // Remove discussion URLs but keep text
  sanitized = sanitized.replace(
    /<span discussion-urls="[^"]*">([^<]*)<\/span>/gi,
    "$1",
  );

  // Remove other HTML tags but keep content
  sanitized = sanitized.replace(
    /<\/?(?!code|pre|strong|em|u|s|a|li|ul|ol|h[1-6]|p|br)[^>]+>/gi,
    "",
  );

  return sanitized;
}

function hasComplexContent(content: string) {
  return /<table|discussion-urls/i.test(content);
}

export function NotionDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();

  const { pageId } = usePaneParams(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL);

  const paneLocation = usePaneLocation("rightPane");
  const entityFromState = paneLocation?.state?.entity;

  const {
    connectionId,
    isLoading: isConnectionLoading,
    hasConnection,
  } = useNotionConnection();

  const {
    data: pageData,
    isLoading: isPageLoading,
    error,
    refetch,
  } = useNotionPageFetch({
    connectionId,
    pageId,
    enabled: !!connectionId,
  });
  const updateMutation = useNotionPageUpdate();

  const displayWebLink = pageData?.web_link || entityFromState?.data?.web_link;

  const [editContent, setEditContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sanitizedContent = useMemo(() => {
    if (!pageData?.body) return "";
    return sanitizeNotionContent(pageData.body);
  }, [pageData?.body]);

  const showComplexContentBanner = useMemo(() => {
    return pageData?.body && hasComplexContent(pageData.body);
  }, [pageData?.body]);

  useEffect(() => {
    setEditContent("");
    setHasUnsavedChanges(false);
  }, [pageId]);

  const handleContentChange = useCallback(
    (markdown: string) => {
      setEditContent(markdown);
      setHasUnsavedChanges(markdown !== (pageData?.body || ""));
    },
    [pageData?.body],
  );

  const handleSave = useCallback(async () => {
    if (!hasUnsavedChanges || !editContent || !connectionId) return;

    try {
      await updateMutation.mutateAsync({
        connectionId,
        pageId,
        data: { command: "replace_content", new_str: editContent },
      });
      setEditContent("");
      setHasUnsavedChanges(false);
      toast.success("Page updated successfully");
      refetch();
    } catch (error) {
      console.error("Failed to save page:", error);
      toast.error("Failed to save page");
    }
  }, [
    connectionId,
    pageId,
    editContent,
    hasUnsavedChanges,
    updateMutation,
    refetch,
  ]);

  const handleCancel = useCallback(() => {
    setEditContent("");
    setHasUnsavedChanges(false);
    refetch();
  }, [refetch]);

  const handleRefresh = useCallback(async () => {
    setEditContent("");
    setHasUnsavedChanges(false);
    await refetch();
    toast.success("Page refreshed");
  }, [refetch]);

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape" && hasUnsavedChanges) {
        e.preventDefault();
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleSave, handleCancel, hasUnsavedChanges]);

  const isLoading = isConnectionLoading || isPageLoading;

  if (!isConnectionLoading && !hasConnection) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="text-muted-foreground text-center">
              <p className="font-medium">No Notion connection found</p>
              <p className="text-sm mt-2">
                Connect Notion in Settings to view Notion pages
              </p>
            </div>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  if (error && !isLoading) {
    return (
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            <div className="flex-1" />
            <TooltipProvider>
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={handleClose}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="text-muted-foreground">
              Failed to load Notion page
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </PaneLayoutContent>
      </PaneLayout>
    );
  }

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="notion"
              name="Notion"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {pageData?.title || "Notion page"}
            </span>
          </div>
          <TooltipProvider>
            {hasUnsavedChanges && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    onClick={handleCancel}
                    className="shrink-0"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Discard changes</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="toolbarGhost"
                  size="icon-sm"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || updateMutation.isPending}
                  className="shrink-0"
                >
                  <Save className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Save</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="toolbarGhost"
                  size="icon-sm"
                  className="shrink-0"
                  onClick={handleRefresh}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>

            {displayWebLink && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    asChild
                  >
                    <a
                      href={displayWebLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open in Notion</TooltipContent>
              </Tooltip>
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0 flex flex-col h-full overflow-hidden">
        {showComplexContentBanner && (
          <Alert className="rounded-none border-x-0 border-t-0">
            <Info className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              This page contains tables or advanced formatting.
              {displayWebLink && (
                <a
                  href={displayWebLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium hover:text-foreground"
                >
                  Open in Notion
                </a>
              )}
              for full experience.
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="flex-1 overflow-hidden px-6 py-4 space-y-4">
            <Skeleton className="h-10 w-3/4" />

            <div className="space-y-3 pt-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>

            <div className="space-y-3 pt-6">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>

            <div className="space-y-3 pt-6">
              <Skeleton className="h-6 w-2/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            <RichTextEditor
              key={`notion-editor-${pageId}`}
              value={editContent || sanitizedContent}
              onChange={handleContentChange}
              placeholder="Start writing..."
              showToolbar={false}
              mode="rich"
              className="h-full border-0 rounded-none flex-1"
              editorClassName="min-h-full max-h-none px-6 py-4 overflow-y-auto"
            />
          </div>
        )}
      </PaneLayoutContent>
    </PaneLayout>
  );
}
